// DiaryFilter — single author/privacy chip rail (W32 mobile polish).
//
// Replaces the legacy `<input type="checkbox">含私人日記</input>` control on
// /diaries.  One `FilterChipGroup`, `layout="swipe"` on mobile,
// `accent="ink-1"` for the active-state fill.
//
// (W41.0 A2) This file used to point at the anniversary page's own chip rail as
// its precedent. That rail was deleted in W40.1 F5, so the comparison had
// stopped meaning anything — the two screens no longer share a shape to copy.
// The contract below is now the record on its own.
//
// 4 chips cover the common privacy/author scopes without dead-ending users:
//   - 「全部」      — default; backend returns shared + author's own (excludes
//                     partner's private per `test_diary.py::test_list_diaries_filters_private_from_partner`).
//   - 「含私人」    — include the author's own private diaries.
//   - 「我的」      — author == current user, all privacy levels.
//   - 「對方的」    — author != current user (partner-authored rows only).
//
// The 「含私人」 + 「我的」 axes overlap by design — 「我的」 already implies
// "see your privates" because privates from anyone else are filtered by the
// backend.  Keeping the chip discrete matches user mental model of "show me
// my drafts / private journals" without forcing them to learn the privacy
// taxonomy first.

import { FilterChipGroup } from "@/components/primitives/FilterChipGroup";

export type DiaryScopeFilter = "all" | "with-private" | "mine" | "partner";

export interface DiaryFilterProps {
  scope: DiaryScopeFilter;
  onScopeChange: (next: DiaryScopeFilter) => void;
  /** Hide 「對方的」 chip if the user is solo (no pair). */
  hidePartnerChip?: boolean;
}

const BASE_ITEMS = [
  { id: "all", label: "全部" },
  { id: "with-private", label: "含私人" },
  { id: "mine", label: "我的" },
  { id: "partner", label: "對方的" },
] as const;

export function DiaryFilter({ scope, onScopeChange, hidePartnerChip = false }: DiaryFilterProps) {
  const items = hidePartnerChip
    ? BASE_ITEMS.filter((i) => i.id !== "partner")
    : BASE_ITEMS;
  return (
    <div
      data-component="DiaryFilter"
      className="flex flex-col gap-s-3"
      data-testid="diary-filter"
    >
      <FilterChipGroup
        items={[...items]}
        selectedId={scope}
        onSelect={(id) => onScopeChange(id as DiaryScopeFilter)}
        accent="ink-1"
        ariaLabel="日記範圍篩選"
        layout="swipe"
      />
    </div>
  );
}
